import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Modal as BSModal, Button, Table} from 'react-bootstrap';
import Modal from "../../components/modal/modal";
import axiosInstance from "../../AxiosInstance";
import {formatTimestamp} from "../../utils";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faDownload, faUpload, faX} from "@fortawesome/free-solid-svg-icons";
import {DangerButton} from "../../components/input/inputs";
import MyContext from "../../MyContext";
import {
    closeNecDetail,
    openConfirmDeleteFile,
    openConfirmDeleteInvoice,
    openInvoiceDetail,
    openOcDetail,
    openUploadDocument,
} from '../bus/modalApi';
import premium from "../invoice_detail/invoiceDetailModal.module.css";
import { PremiumCardRow } from '../shared/PremiumCardRow.jsx';
import { formatCurrencyAmount } from '../../utils/currency';

function billsForNec(oc, necId) {
    if (!oc?.bills?.length || necId == null) return [];
    return oc.bills.filter((x) => x.order_detail?.id === necId);
}

/** Alineado con invoice_service.disassociate_invoice: no aceptado (2), no pagado (1). */
function billEligibleForDisassociate(b) {
    if (Number(b?.eval_status) === 2) return false;
    if (Number(b?.payment_status) === 1) return false;
    return true;
}

function billsEligibleForDisassociate(necBills) {
    return necBills.filter(billEligibleForDisassociate);
}

function invoiceTypeLabel(invType) {
    if (invType === 0) return 'Boleta';
    if (invType === 1) return 'Factura';
    if (invType === 2) return 'RHE';
    return '-';
}

function getBillIgvAmount(bill) {
    const igvTotal = (bill?.extra_data ?? []).reduce((acc, item) => {
        const value = Number(item?.monto_igv);
        return Number.isFinite(value) ? acc + value : acc;
    }, 0);
    if (!igvTotal) return '-';
    return formatCurrencyAmount(bill?.currency, igvTotal) || '-';
}

function getNecPendingAmountLabel(nec, necBills) {
    const invoicesTotal = (necBills ?? []).reduce((acc, bill) => acc + Number(bill?.total || 0), 0);
    const necTotal = Number(nec?.total || 0);
    return formatCurrencyAmount(nec?.currency, Math.abs(invoicesTotal - necTotal));
}

function getOseSunatStatusDescriptor(bill) {
    if (bill?.invoice_type !== 1) return { label: 'Aceptado', tone: 'success' };
    switch (bill?.sunat_status) {
        case -1:
            return { label: 'Cargando...', tone: 'muted' };
        case -2:
            return { label: 'No disponible', tone: 'muted' };
        case '0':
            return { label: 'No existe', tone: 'neutral' };
        case '1':
            return { label: 'Aceptado', tone: 'success' };
        case '2':
            return { label: 'Anulado', tone: 'danger' };
        case '3':
            return { label: 'Autorizado', tone: 'success' };
        case '4':
            return { label: 'No autorizado', tone: 'danger' };
        default:
            return { label: 'Pendiente', tone: 'muted' };
    }
}

function getConformityStatusDescriptor(bill) {
    if (bill?.invoice_type !== 1) return { label: 'Conforme', tone: 'success' };
    switch (bill?.conformity_status) {
        case -1:
            return { label: 'Cargando...', tone: 'muted' };
        case -2:
            return { label: 'No disponible', tone: 'muted' };
        case '01':
        case '06':
            return { label: 'Pendiente', tone: 'muted' };
        case '03':
            return { label: 'Subsanado', tone: 'warning' };
        case '04':
            return { label: 'Conforme', tone: 'success' };
        case '02':
            return { label: 'Disconforme', tone: 'danger' };
        case '05':
            return { label: 'No válido', tone: 'danger' };
        default:
            return { label: 'Pendiente', tone: 'muted' };
    }
}

export default function NECDetailModal({show, setter, oc, currentNEC, bill, userType}) {
    const systemSettings = JSON.parse(localStorage.getItem('system_settings'));
    const {isClient, user} = useContext(MyContext);

    const necBills = useMemo(() => billsForNec(oc, currentNEC?.id), [oc, currentNEC?.id]);
    const eligibleBills = billsEligibleForDisassociate(necBills);
    const necHasAcceptedInvoice = necBills.some((b) => Number(b.eval_status) === 2);

    const [showDisassociatePicker, setShowDisassociatePicker] = useState(false);
    const [selectedBillId, setSelectedBillId] = useState(null);
    const [sunatStatusByBillId, setSunatStatusByBillId] = useState({});

    useEffect(() => {
        if (!show || !necBills.length) return undefined;
        let cancelled = false;

        necBills
            .filter((bRow) => bRow.invoice_type === 1)
            .forEach((bRow) => {
                const existingBill = bill?.id === bRow.id ? bill : bRow;
                const hasStatus =
                    existingBill?.sunat_status !== undefined ||
                    existingBill?.conformity_status !== undefined;

                setSunatStatusByBillId((prev) => ({
                    ...prev,
                    [bRow.id]: hasStatus
                        ? {
                            sunat_status: existingBill.sunat_status,
                            conformity_status: existingBill.conformity_status,
                        }
                        : {
                            ...(prev[bRow.id] ?? {}),
                            sunat_status: -1,
                            conformity_status: -1,
                        },
                }));

                axiosInstance
                    .get(`sunat/${bRow.id}/`)
                    .then((response) => {
                        if (cancelled || response?.status !== 200 || !response?.data) return;
                        setSunatStatusByBillId((prev) => ({
                            ...prev,
                            [bRow.id]: {
                                sunat_status: response.data.ose_sunat_status,
                                conformity_status: response.data.conformity_status,
                            },
                        }));
                    })
                    .catch(() => {
                        if (cancelled) return;
                        setSunatStatusByBillId((prev) => ({
                            ...prev,
                            [bRow.id]: {
                                ...(prev[bRow.id] ?? {}),
                                sunat_status: -2,
                                conformity_status: -2,
                            },
                        }));
                    });
            });

        return () => {
            cancelled = true;
        };
    }, [show, currentNEC?.id, necBills, bill]);

    const openDisassociatePicker = () => {
        const list = billsEligibleForDisassociate(billsForNec(oc, currentNEC?.id));
        setSelectedBillId(list[0]?.id ?? null);
        setShowDisassociatePicker(true);
    };

    const closeDisassociatePicker = () => {
        setShowDisassociatePicker(false);
        setSelectedBillId(null);
    };

    const getBillWithSunatStatus = (bRow) => ({
        ...bRow,
        ...(bill?.id === bRow.id
            ? {
                sunat_status: bill.sunat_status,
                conformity_status: bill.conformity_status,
            }
            : {}),
        ...(sunatStatusByBillId[bRow.id] ?? {}),
    });

    const renderTableStatus = ({ label, tone }) => (
        <span
            className={`${premium.tableStatusBadge} ${
                premium[`tableStatus${tone.charAt(0).toUpperCase()}${tone.slice(1)}`]
            }`}
        >
            {label}
        </span>
    );

    const openEvaluation = (foundBill) => {
        const billWithStatus = getBillWithSunatStatus(foundBill);
        openInvoiceDetail({
            bill: billWithStatus,
            returnTo: { oc, nec: currentNEC, bill: billWithStatus, userType },
        });
        closeNecDetail();
    };

    const renderDocuments = () => {
        return (
            <>
                {necBills.length === 0 ? (
                    <p className={`${premium.emptyCell} mb-3`}>Sin comprobantes asociados a esta NEC.</p>
                ) : (
                    <table className={`${premium.modernTable} mb-3`}>
                        <thead>
                            <tr>
                                <th>Tipo</th>
                                <th>Serie - Nº</th>
                                <th>Fecha emisión</th>
                                <th className="text-end">Total</th>
                                <th className="text-end">IGV</th>
                                <th>OSE/SUNAT</th>
                                <th>Conformidad</th>
                                {isClient ? <th>Evaluar</th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {necBills.map((bRow) => {
                                const statusBill = getBillWithSunatStatus(bRow);

                                return (
                                    <tr key={bRow.id}>
                                        <td>{invoiceTypeLabel(bRow.invoice_type)}</td>
                                        <td>{bRow.sn ?? '-'}</td>
                                        <td>{bRow.issue_date ?? '-'}</td>
                                        <td className="text-end">
                                            {formatCurrencyAmount(bRow.currency, bRow.total)}
                                        </td>
                                        <td className="text-end">{getBillIgvAmount(bRow)}</td>
                                        <td>{renderTableStatus(getOseSunatStatusDescriptor(statusBill))}</td>
                                        <td>{renderTableStatus(getConformityStatusDescriptor(statusBill))}</td>
                                        {isClient ? (
                                            <td>
                                                <button
                                                    type="button"
                                                    className={premium.tableActionBtn}
                                                    onClick={() => openEvaluation(bRow)}
                                                >
                                                    <FontAwesomeIcon icon={faArrowRight} aria-hidden />
                                                    Evaluar
                                                </button>
                                            </td>
                                        ) : null}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {necBills.length > 0 ? (
                    <div className={premium.tableSummaryRow}>
                        <span>Monto Pendiente NEC</span>
                        <strong>{getNecPendingAmountLabel(currentNEC, necBills)}</strong>
                    </div>
                ) : null}
            </>
        );
    };

    const downloadFile = (id, name) => {
        axiosInstance.get(`file/${id}/`, {
            responseType: 'blob'
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${name}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
    }

    const deleteFile = (id) => {
        openConfirmDeleteFile({ fileId: id });
    }

    const handlePickerContinue = () => {
        const chosen = eligibleBills.find((b) => b.id === selectedBillId);
        if (!chosen) return;
        closeDisassociatePicker();
        openConfirmDeleteInvoice({
            bill: chosen,
            intent: 'disassociate',
            returnTo: {
                ocId: oc?.id,
                necId: currentNEC?.id,
                userType,
            },
        });
    }

    const openUpload = () => {
        const newest = necBills.reduce(
            (best, cur) => (!best || cur.id > best.id ? cur : best),
            null
        );
        closeNecDetail();
        openUploadDocument({
            oc,
            nec: currentNEC,
            currentInvoiceName: newest?.sn || '',
            userType,
        });
    }

    const modalTitle = oc?.number ? `Detalles de la NEC (${oc.number})` : `Detalles de la NEC`;

    return (
        <>
        <Modal
            show={show}
            setter={setter}
            name={modalTitle}
            size={'xl'}
            hideClose={true}
            headerClassName={premium.headerPremium}
            titleClassName={premium.titlePremium}
            dialogClassName={premium.dialogWide}
        >
            <div className={premium.bodyWrap}>
                <div className={premium.modalSubtitle}>Datos</div>
                <div className={premium.compactGrid}>
                    <PremiumCardRow label="N° NEC:">{currentNEC.nec_number}</PremiumCardRow>
                    <PremiumCardRow label="RUC del cliente:">{oc.client.ruc}</PremiumCardRow>
                    <PremiumCardRow label="Razón Social del cliente:" span2>{oc.client.name}</PremiumCardRow>
                    <PremiumCardRow label="Monto:">
                        {formatCurrencyAmount(currentNEC.currency, currentNEC.total)}
                    </PremiumCardRow>
                    <PremiumCardRow label="Moneda:">{currentNEC.currency.name}</PremiumCardRow>
                </div>

                <div className={premium.modalSubtitle}>Detalle NEC</div>
                <table className={premium.modernTable}>
                    <thead>
                        <tr>
                            <th>N°</th>
                            <th>Cantidad</th>
                            <th>Descripción</th>
                            <th>Precio Unitario</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentNEC.nec_details.map((nec, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{nec.quantity}</td>
                                <td>{nec.description}</td>
                                <td>{formatCurrencyAmount(nec.currency, nec.unit_price)}</td>
                                <td>{formatCurrencyAmount(nec.currency, nec.total)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {oc.type !== undefined && <div className={'my-4'}>
                    <div className={`${premium.modalSubtitle} d-flex justify-content-between align-items-center flex-wrap gap-2`}>
                        <span>Documentos Asociados</span>
                        {isClient && eligibleBills.length > 0 ? (
                            <DangerButton
                                variant="destructive"
                                name="Desasociar"
                                onClick={openDisassociatePicker}
                            />
                        ) : null}
                    </div>
                    <div>{renderDocuments()}</div>
                </div>
                }

                <div className={premium.modalSubtitle}>Archivos de la NEC</div>
                <table className={premium.modernTable}>
                    <thead>
                        <tr>
                            <th>Fecha de Subida</th>
                            <th>Tipo de Documento</th>
                            <th>Nombre del Archivo</th>
                            <th>Descargar</th>
                            <th>Eliminar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentNEC.files.length === 0 ? (
                            <tr>
                                <td colSpan={5} className={premium.emptyCell}>No hay archivos</td>
                            </tr>
                        ) : (
                            currentNEC.files.map((file, index) => (
                                <tr key={index}>
                                    <td>{formatTimestamp(file.upload_date)}</td>
                                    <td>{file.type}</td>
                                    <td>{file.name}</td>
                                    <td>
                                        <div style={{
                                            margin: 'auto',
                                            width: "28px",
                                            height: "28px",
                                            borderRadius: '6px',
                                            backgroundColor: systemSettings.main_color ? systemSettings.main_color : "#FF1235",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }} role={'button'} tabIndex={0} onClick={() => {downloadFile(file.id, file.name);}}
                                             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') downloadFile(file.id, file.name); }}
                                        >
                                            <FontAwesomeIcon icon={faDownload} color={'#FFF'}/>
                                        </div>
                                    </td>
                                    <td>
                                        {(file.type !== "Factura" && !necHasAcceptedInvoice) && (
                                            <div style={{
                                                margin: 'auto',
                                                width: "28px",
                                                height: "28px",
                                                borderRadius: '6px',
                                                backgroundColor: systemSettings.main_color ? systemSettings.main_color : "#FF1235",
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }} role={'button'} tabIndex={0} onClick={() => {deleteFile(file.id);}}
                                                 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') deleteFile(file.id); }}
                                            >
                                                <FontAwesomeIcon icon={faX} color={'#FFF'}/>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className={premium.footerBar}>
                <div className={premium.footerRight}>
                    {(!isClient || (isClient && user?.role?.can_upload_provider_files)) ? (
                        <button
                            type="button"
                            className={premium.footerModalBtn}
                            style={{ backgroundColor: '#01C7E2' }}
                            onClick={openUpload}
                        >
                            <FontAwesomeIcon icon={faUpload} className={'pe-2'} aria-hidden/>
                            Subir Archivo
                        </button>
                    ) : null}

                    <DangerButton onClick={() => {
                        closeNecDetail();
                        openOcDetail({ oc, userType });
                    }} name={'Cerrar'}/>
                </div>
            </div>
        </Modal>

        <BSModal
            show={showDisassociatePicker}
            onHide={closeDisassociatePicker}
            centered
            backdrop="static"
        >
            <BSModal.Header closeButton>
                <BSModal.Title>Desasociar comprobante</BSModal.Title>
            </BSModal.Header>
            <BSModal.Body>
                <p className="text-muted small mb-3">
                    Elija qué comprobante desea desasociar de esta NEC. No es lo mismo que eliminar un archivo en «Archivos de la NEC».
                </p>
                <Table hover size="sm" className="mb-0 align-middle">
                    <thead>
                        <tr>
                            <th style={{ width: 40 }} aria-label="Seleccionar" />
                            <th>Tipo</th>
                            <th>Serie - Nº</th>
                            <th>Fecha emisión</th>
                            <th className="text-end">Total</th>
                            <th className="text-end">IGV</th>
                        </tr>
                    </thead>
                    <tbody>
                        {eligibleBills.map((bRow) => (
                            <tr key={bRow.id}>
                                <td>
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        name="disassociate-bill"
                                        checked={selectedBillId === bRow.id}
                                        onChange={() => setSelectedBillId(bRow.id)}
                                    />
                                </td>
                                <td>{invoiceTypeLabel(bRow.invoice_type)}</td>
                                <td>{bRow.sn ?? '-'}</td>
                                <td>{bRow.issue_date ?? '-'}</td>
                                <td className="text-end">
                                    {formatCurrencyAmount(bRow.currency, bRow.total)}
                                </td>
                                <td className="text-end">{getBillIgvAmount(bRow)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </BSModal.Body>
            <BSModal.Footer className="d-flex gap-2 justify-content-end flex-wrap">
                <Button variant="secondary" type="button" onClick={closeDisassociatePicker}>
                    Cancelar
                </Button>
                <Button variant="danger" type="button" disabled={selectedBillId == null} onClick={handlePickerContinue}>
                    Continuar
                </Button>
            </BSModal.Footer>
        </BSModal>
        </>
    )
}
